import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import NavbarMoment from "../../../../components/NavbarMovement";
import CodeReaderField from "../../../../components/CodeReaderField";
import MovementTable from "../../../../components/MovementTable";
import Button from "../../../../components/Button";
import TextFieldRow from "../../../../components/TextFieldRow";
import { useAuth } from "../../../../context/auth-context";

type MovementType = "Serviço" | "Estoque" | "Perda" | "Consumo" | "Venda";
type TipoSaida = "servico" | "perda" | "consumo" | "venda";

type TableRow = {
  codigo: string;
  nomeProduto: string;
  quantidadeEstoque: number;
  quantidadeSelecionada: number;
};

type RowMeta = {
  productId: number;
  custoUnitarioDefault?: string | null;
};

type EstoqueDepartamentoKey =
  | "suprimentos"
  | "suprimentos-campos"
  | "recursos-humanos"
  | "tecnicos"
  | "generico";

type MovimentacaoConfig = {
  key: EstoqueDepartamentoKey;
  slug: string;
  departamentoOrigem: string;
  produtoDepartamento: string;
  pathBases: string[];
  title?: string;
  defaultMovement: MovementType;
  options: MovementType[];
  enableServicoEmpresaCoordenacao?: boolean;
};

type EmpresaOption = {
  id: number;
  nome_fantasia: string;
};

type CoordenadorOption = {
  id: number;
  nome: string;
  empresas_responsavel: EmpresaOption[];
};

type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

type ProdutoAnyAPI = {
  id: number | string;
  codigo: string;
  nome: string;

  ncm?: string | null;
  classificacao?: string | null;
  posicao?: string | null;
  vendavel?: boolean | null;
  responsavel_registro?: number | string | null;
  departamento_legado?: string | null;
  peso?: string | number | null;
  dimensao?: string | null;
  criado_em?: string | null;
  atualizado_em?: string | null;
  ocultar?: boolean | null;
  empresa_id?: number | string | null;

  estoque_id?: number | string | null;
  estoque_codigo?: string | null;
  estoque_nome?: string | null;

  modulo_id?: number | string | null;
  modulo_codigo?: string | null;
  modulo_label?: string | null;

  quantidade_atual?: number | string | null;
  custo_unitario?: string | number | null;
  custo_unitario_atual?: string | number | null;
  moeda?: string | null;
};

type GetProdutoResp = {
  ok: boolean;
  erro?: string;
  erros?: string[];

  fonte?: "cache" | "banco";
  versao?: number;

  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    empresa_id?: number | string;
    modulo_id?: number | string;
  };

  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
    icone?: string | null;
    path?: string | null;
  };

  data?: ProdutoAnyAPI[] | null;
  visiveis?: ProdutoAnyAPI[] | null;
  ocultos?: ProdutoAnyAPI[] | null;
};

type SaidaMovimentarResp = {
  ok: boolean;
  erro?: string;
  erros?: string[];
  data?: {
    movimentacao?: {
      id?: number | string;
    };
    tipo_saida?: string;
    codigo_movimentacao?: string;
  };
};

const API_LOGVI_BASE = "https://api.logvi-havox.com";

const URL_GET_PRODUTO = `${API_LOGVI_BASE}/template/estoque-administrativo/get-produto`;

const URL_POST_SAIDA_MOVIMENTAR = `${API_LOGVI_BASE}/template/estoque-administrativo/post-saida/movimentar`;

const COLABS_URL =
  "https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaboradores-nome";

const COORDS_URL =
  "https://api.logvi-havox.com/departamentos/recursos-humanos/colaboradores-coordenacao";

const EMPRESAS_URL =
  "https://api.eva-googlemarine.com/geral/empresa-clientes-nome";

const ACCESS_TOKEN_KEY = "access_token";

const ESTOQUE_SLUG_TO_MODULO_CODIGO: Record<string, string> = {
  "estoque-p-servico-e-comercial": "suprimentos-estoque",
  "estoque-p-servico-campos": "suprimentos-estoque-campos",
  "estoque-p-uso-coorporativo": "rh-estoque",
  "estoque-p-tecnicos-e-laboratorio": "tec-estoque",
  "materiais-p-venda": "suprimentos-estoque",
  "coordenacao-rastreio-de-materiais": "coordenacao",
};

const MOVIMENTACAO_CONFIGS: MovimentacaoConfig[] = [
  {
    key: "suprimentos",
    slug: "estoque-p-servico-e-comercial",
    departamentoOrigem: "suprimentos",
    produtoDepartamento: "suprimentos",
    pathBases: [
      "/suprimentos/estoque-p-servico-e-comercial/estoque/realizar-movimentacao",
      "/diretoria/estoque/realizar-movimentacao",
    ],
    defaultMovement: "Serviço",
    options: ["Serviço", "Perda", "Consumo", "Venda"],
    enableServicoEmpresaCoordenacao: true,
  },
  {
    key: "suprimentos-campos",
    slug: "estoque-p-servico-campos",
    departamentoOrigem: "suprimentos-campos",
    produtoDepartamento: "suprimentos-campos",
    pathBases: [
      "/suprimentos/estoque-p-servico-campos/estoque/realizar-movimentacao",
    ],
    defaultMovement: "Serviço",
    options: ["Serviço", "Perda", "Consumo", "Venda"],
    enableServicoEmpresaCoordenacao: true,
  },
  {
    key: "recursos-humanos",
    slug: "estoque-p-uso-coorporativo",
    departamentoOrigem: "recursos-humanos",
    produtoDepartamento: "recursos-humanos",
    pathBases: [
      "/suprimentos/estoque-p-uso-coorporativo/estoque/realizar-movimentacao",
      "/diretoria/estoque-recursos-humanos/realizar-movimentacao",
    ],
    defaultMovement: "Consumo",
    options: ["Consumo", "Perda"],
    enableServicoEmpresaCoordenacao: false,
  },
  {
    key: "tecnicos",
    slug: "estoque-p-tecnicos-e-laboratorio",
    departamentoOrigem: "tecnico",
    produtoDepartamento: "tecnicos",
    pathBases: [
      "/suprimentos/estoque-p-tecnicos-e-laboratorio/estoque/realizar-movimentacao",
      "/diretoria/estoque-laboratorio/realizar-movimentacao",
    ],
    defaultMovement: "Serviço",
    options: ["Serviço", "Perda", "Consumo", "Venda"],
    enableServicoEmpresaCoordenacao: true,
  },
];

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function getConfigByPath(pathname: string): MovimentacaoConfig | null {
  const normalized = pathname.replace(/\/+$/, "");

  const found = MOVIMENTACAO_CONFIGS.find((config) =>
    config.pathBases.some((path) => {
      const base = path.replace(/\/+$/, "");
      return normalized === base || normalized.startsWith(`${base}/`);
    })
  );

  return found ?? null;
}

function getConfigBySlug(estoqueSlug?: string): MovimentacaoConfig | null {
  const slug = normalizeSlug(estoqueSlug);

  if (!slug) return null;

  const found = MOVIMENTACAO_CONFIGS.find((config) => config.slug === slug);

  if (found) return found;

  return {
    key: "generico",
    slug,
    departamentoOrigem: slug,
    produtoDepartamento: slug,
    pathBases: [`/suprimentos/${slug}/estoque/realizar-movimentacao`],
    defaultMovement: "Serviço",
    options: ["Serviço", "Perda", "Consumo", "Venda"],
    enableServicoEmpresaCoordenacao: true,
  };
}

function getConfig(params: {
  estoqueSlug?: string;
  pathname: string;
}): MovimentacaoConfig {
  const bySlug = getConfigBySlug(params.estoqueSlug);

  if (bySlug) return bySlug;

  const byPath = getConfigByPath(params.pathname);

  if (byPath) return byPath;

  return MOVIMENTACAO_CONFIGS[0];
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function nowAsSql() {
  return new Date().toISOString().slice(0, 19).replace("T", " ");
}

function moneyToNumber(v?: string | number | null) {
  if (v == null) return 0;

  const n = Number(String(v).replace(",", "."));

  return Number.isFinite(n) ? n : 0;
}

function movementToTipoSaida(selected: MovementType): TipoSaida | null {
  switch (selected) {
    case "Serviço":
      return "servico";
    case "Perda":
      return "perda";
    case "Consumo":
      return "consumo";
    case "Venda":
      return "venda";
    default:
      return null;
  }
}

async function readJsonSafe<T = any>(resp: Response): Promise<T | null> {
  const text = await resp.text();

  if (!text) return null;

  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

async function postJsonAuth<T = any>(url: string, payload: any) {
  const resp = await fetch(url, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(payload),
  });

  const json = await readJsonSafe<T>(resp);

  return {
    resp,
    json,
  };
}

function findModuloAtualFromSessao(
  sessao: any,
  areaSlug?: string,
  estoqueSlug?: string
): ModuloAtual | null {
  const area = normalizeComparable(areaSlug || "suprimentos");
  const estoque = normalizeComparable(
    estoqueSlug || "estoque-p-servico-e-comercial"
  );

  const expectedModuloCodigo =
    ESTOQUE_SLUG_TO_MODULO_CODIGO[estoque] || estoque;

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosOrdenados = [
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) === enderecoSelecionadoId : false
    ),
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) !== enderecoSelecionadoId : true
    ),
  ];

  for (const endereco of enderecosOrdenados) {
    const departamentos = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of departamentos) {
      const depSlug = normalizeComparable(dep?.slug ?? dep?.routeKey);

      if (area && depSlug && depSlug !== area) {
        continue;
      }

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const foundByCodigo = modulos.find((mod: any) => {
        const modCodigo = normalizeComparable(mod?.codigo);
        return modCodigo === normalizeComparable(expectedModuloCodigo);
      });

      const found =
        foundByCodigo ||
        modulos.find((mod: any) => {
          const modCodigo = normalizeComparable(mod?.codigo);
          const modLabel = normalizeComparable(mod?.label);

          return (
            modCodigo === estoque ||
            modLabel === estoque ||
            modCodigo.includes(estoque) ||
            estoque.includes(modCodigo)
          );
        });

      if (found?.id !== undefined && found?.id !== null) {
        const id = Number(found.id);

        if (Number.isInteger(id) && id > 0) {
          return {
            id,
            codigo: String(found.codigo || ""),
            label: String(found.label || ""),
          };
        }
      }
    }
  }

  return null;
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    minHeight: "100%",
    boxSizing: "border-box",
    padding: "14px 16px 18px",
    background: "transparent",
  },

  shell: {
    width: "100%",
    maxWidth: 1180,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    boxSizing: "border-box",
  },

  topBlock: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  movementBlock: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid rgba(15, 23, 42, 0.10)",
    background: "rgba(255, 255, 255, 0.88)",
    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.06)",
    padding: "10px 12px",
    overflow: "visible",
  },

  tableBlock: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid rgba(15, 23, 42, 0.10)",
    background: "rgba(255, 255, 255, 0.92)",
    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.055)",
    padding: 8,
    overflowX: "auto",
    overflowY: "visible",
  },

  actions: {
    width: "100%",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "stretch",
    gap: 8,
    paddingTop: 2,
    boxSizing: "border-box",
  },
};

const compactCss = `
  .movimentacao-generica {
    font-family: "Nunito", "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    color: #0f172a;
  }

  .movimentacao-generica *,
  .movimentacao-generica *::before,
  .movimentacao-generica *::after {
    box-sizing: border-box;
  }

  .movimentacao-generica :where(input, select, textarea) {
    min-height: 32px !important;
    height: 32px;
    font-size: 12px !important;
    line-height: 1.2 !important;
    padding: 5px 8px !important;
    border-radius: 0 !important;
  }

  .movimentacao-generica :where(label) {
    font-size: 11px !important;
    line-height: 1.15 !important;
    margin-bottom: 3px !important;
  }

  .movimentacao-generica :where(button) {
    min-height: 32px !important;
    height: 32px;
    padding: 6px 12px !important;
    font-size: 11px !important;
    line-height: 1.1 !important;
    border-radius: 2px !important;
  }

  .movimentacao-generica .movimentacao-actions {
    width: 100% !important;
  }

  .movimentacao-generica .movimentacao-actions > * {
    width: 100% !important;
    max-width: none !important;
    flex: 1 1 100% !important;
  }

  .movimentacao-generica .movimentacao-actions :where(button) {
    width: 100% !important;
    min-width: 0 !important;
    max-width: none !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: #0c333c !important;
    color: #ffffff !important;
    border: 1px solid rgba(12, 51, 60, 0.92) !important;
    font-weight: 700 !important;
    letter-spacing: 0.01em;
    box-shadow: 0 8px 18px rgba(12, 51, 60, 0.16) !important;
  }

  .movimentacao-generica .movimentacao-actions :where(button:disabled) {
    opacity: 0.48 !important;
    cursor: not-allowed !important;
    box-shadow: none !important;
  }

  .movimentacao-generica :where(table) {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px !important;
  }

  .movimentacao-generica :where(th) {
    height: 28px !important;
    padding: 5px 6px !important;
    font-size: 10.5px !important;
    line-height: 1.1 !important;
    white-space: nowrap;
  }

  .movimentacao-generica :where(td) {
    height: 28px !important;
    padding: 4px 6px !important;
    font-size: 10.5px !important;
    line-height: 1.1 !important;
    vertical-align: middle;
  }
`;

type ServicoProps = {
  enableEmpresaCoordenacao?: boolean;

  numeroOS: string;
  setNumeroOS: (v: string) => void;

  nomeEmbarcacao: string;
  setNomeEmbarcacao: (v: string) => void;

  responsavelLiberacao: string;
  setResponsavelLiberacao: (v: string) => void;

  responsavelColeta: string;
  setResponsavelColeta: (v: string) => void;

  nomeEmpresa: string;
  setNomeEmpresa: (v: string) => void;

  empresaClienteId: number | null;
  setEmpresaClienteId: (v: number | null) => void;

  coordenadoresResponsaveisIds: number[];
  setCoordenadoresResponsaveisIds: (v: number[]) => void;

  coordenadoresResponsaveisNomes: string[];
  setCoordenadoresResponsaveisNomes: (v: string[]) => void;
};

const ServicoMovement: React.FC<ServicoProps> = ({
  enableEmpresaCoordenacao = false,

  numeroOS,
  setNumeroOS,

  nomeEmbarcacao,
  setNomeEmbarcacao,

  responsavelLiberacao,
  setResponsavelLiberacao,

  responsavelColeta,
  setResponsavelColeta,

  nomeEmpresa,
  setNomeEmpresa,

  empresaClienteId,
  setEmpresaClienteId,

  coordenadoresResponsaveisIds,
  setCoordenadoresResponsaveisIds,

  coordenadoresResponsaveisNomes,
  setCoordenadoresResponsaveisNomes,
}) => {
  const [colabOptions, setColabOptions] = useState<string[]>([]);
  const [loadingColabs, setLoadingColabs] = useState(false);
  const [erroColabs, setErroColabs] = useState<string | null>(null);

  const [coordOptions, setCoordOptions] = useState<CoordenadorOption[]>([]);
  const [loadingCoords, setLoadingCoords] = useState(false);
  const [erroCoords, setErroCoords] = useState<string | null>(null);

  const toUpper = (fn: (v: string) => void) => (v: string) =>
    fn((v || "").toUpperCase());

  useEffect(() => {
    let alive = true;

    const ctrlColabs = new AbortController();
    const ctrlCoords = new AbortController();

    (async () => {
      setLoadingColabs(true);
      setErroColabs(null);

      try {
        const res = await fetch(COLABS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: ctrlColabs.signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();

        const list: string[] = Array.isArray(json?.data)
          ? (json.data as Array<{ nome_completo?: string }>).map((x) =>
              (x?.nome_completo ?? "").trim()
            )
          : [];

        const unique = Array.from(
          new Set(
            list
              .map((s) => (typeof s === "string" ? s.trim().toUpperCase() : ""))
              .filter(Boolean)
          )
        );

        if (alive) setColabOptions(unique);
      } catch (e: any) {
        if (e?.name !== "AbortError" && alive) {
          setErroColabs(String(e?.message || e));
        }
      } finally {
        if (alive) setLoadingColabs(false);
      }

      if (!enableEmpresaCoordenacao) return;

      setLoadingCoords(true);
      setErroCoords(null);

      try {
        const res = await fetch(COORDS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: ctrlCoords.signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();

        const list: CoordenadorOption[] = Array.isArray(json?.data)
          ? (json.data as Array<any>)
              .map((x) => ({
                id: Number(x?.id),
                nome: String(x?.nome || "")
                  .trim()
                  .toUpperCase(),
                empresas_responsavel: Array.isArray(x?.empresas_responsavel)
                  ? x.empresas_responsavel
                      .map((e: any) => ({
                        id: Number(e?.id),
                        nome_fantasia: String(e?.nome_fantasia || "")
                          .trim()
                          .toUpperCase(),
                      }))
                      .filter(
                        (e: EmpresaOption) =>
                          Number.isFinite(e.id) && e.id > 0 && e.nome_fantasia
                      )
                  : [],
              }))
              .filter(
                (x) => Number.isFinite(x.id) && x.id > 0 && x.nome.length > 0
              )
          : [];

        if (alive) setCoordOptions(list);
      } catch (e: any) {
        if (e?.name !== "AbortError" && alive) {
          setErroCoords(String(e?.message || e));
        }
      } finally {
        if (alive) setLoadingCoords(false);
      }
    })();

    return () => {
      alive = false;
      ctrlColabs.abort();
      ctrlCoords.abort();
    };
  }, [enableEmpresaCoordenacao]);

  const colabDropdown = useMemo(
    () => (colabOptions.length ? colabOptions : undefined),
    [colabOptions]
  );

  const empresaOptions = useMemo(() => {
    const map = new Map<number, EmpresaOption>();

    for (const coord of coordOptions) {
      for (const emp of coord.empresas_responsavel || []) {
        if (!map.has(emp.id)) {
          map.set(emp.id, {
            id: emp.id,
            nome_fantasia: emp.nome_fantasia,
          });
        }
      }
    }

    return Array.from(map.values()).sort((a, b) =>
      a.nome_fantasia.localeCompare(b.nome_fantasia, "pt-BR")
    );
  }, [coordOptions]);

  const empresaDropdown = useMemo(
    () => empresaOptions.map((e) => e.nome_fantasia),
    [empresaOptions]
  );

  const coordenadoresDaEmpresaSelecionada = useMemo(() => {
    if (!empresaClienteId) return [];

    return coordOptions.filter((coord) =>
      (coord.empresas_responsavel || []).some(
        (emp) => emp.id === empresaClienteId
      )
    );
  }, [coordOptions, empresaClienteId]);

  const coordenadorDropdown = useMemo(() => {
    return coordOptions.map((coord) => coord.nome);
  }, [coordOptions]);

  const handleEmpresaChange = (value: string) => {
    const empresaNome = String(value || "")
      .trim()
      .toUpperCase();

    setNomeEmpresa(empresaNome);

    const empresa = empresaOptions.find((e) => e.nome_fantasia === empresaNome);

    if (!empresa) {
      setEmpresaClienteId(null);
      setCoordenadoresResponsaveisIds([]);
      setCoordenadoresResponsaveisNomes([]);
      return;
    }

    setEmpresaClienteId(empresa.id);

    const coordsDaEmpresa = coordOptions.filter((coord) =>
      (coord.empresas_responsavel || []).some((emp) => emp.id === empresa.id)
    );

    const coordenadorPadrao = coordsDaEmpresa[0] ?? null;

    if (!coordenadorPadrao) {
      setCoordenadoresResponsaveisIds([]);
      setCoordenadoresResponsaveisNomes([]);
      return;
    }

    setCoordenadoresResponsaveisIds([coordenadorPadrao.id]);
    setCoordenadoresResponsaveisNomes([coordenadorPadrao.nome]);
  };

  const handleCoordenadorChange = (value: string) => {
    const nomeCoord = String(value || "")
      .trim()
      .toUpperCase();

    const coord = coordOptions.find((c) => c.nome === nomeCoord);

    if (!coord) {
      setCoordenadoresResponsaveisIds([]);
      setCoordenadoresResponsaveisNomes(nomeCoord ? [nomeCoord] : []);
      return;
    }

    setCoordenadoresResponsaveisIds([coord.id]);
    setCoordenadoresResponsaveisNomes([coord.nome]);
  };

  const coordenadorHint = useMemo(() => {
    if (loadingCoords) return "CARREGANDO COORDENADORES...";
    if (erroCoords) return "FALHA AO CARREGAR. DIGITE MANUALMENTE.";

    if (!empresaClienteId) {
      return coordenadorDropdown.length
        ? "SELECIONE O COORDENADOR RESPONSÁVEL"
        : "NENHUM COORDENADOR DISPONÍVEL";
    }

    if (coordenadoresDaEmpresaSelecionada.length > 0) {
      return "COORDENADOR PREENCHIDO PELA EMPRESA. VOCÊ PODE ALTERAR.";
    }

    return coordenadorDropdown.length
      ? "EMPRESA SEM COORDENADOR VINCULADO. SELECIONE MANUALMENTE."
      : "NENHUM COORDENADOR DISPONÍVEL";
  }, [
    loadingCoords,
    erroCoords,
    empresaClienteId,
    coordenadorDropdown.length,
    coordenadoresDaEmpresaSelecionada.length,
  ]);

  return (
    <div>
      <TextFieldRow
        center={{
          label: "Destinatário da Coleta",
          value: responsavelColeta,
          onChange: toUpper(setResponsavelColeta),
          placeholder: loadingColabs
            ? "CARREGANDO COLABORADORES..."
            : erroColabs
            ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
            : "SELECIONE O DESTINATÁRIO DA COLETA",
          options: colabDropdown,
        }}
      />

      <TextFieldRow
        center={{
          label: "Responsável pela Liberação",
          value: responsavelLiberacao,
          onChange: toUpper(setResponsavelLiberacao),
          placeholder: loadingColabs
            ? "CARREGANDO COLABORADORES..."
            : erroColabs
            ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
            : "SELECIONE O RESPONSÁVEL PELA LIBERAÇÃO",
          options: colabDropdown,
        }}
      />

      {enableEmpresaCoordenacao && (
        <TextFieldRow
          center={{
            label: "Empresa Cliente",
            value: nomeEmpresa,
            onChange: handleEmpresaChange,
            placeholder: loadingCoords
              ? "CARREGANDO EMPRESAS..."
              : erroCoords
              ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
              : "SELECIONE A EMPRESA CLIENTE",
            options: empresaDropdown.length ? empresaDropdown : undefined,
          }}
        />
      )}

      {enableEmpresaCoordenacao && (
        <TextFieldRow
          center={{
            label: "Coordenador Responsável",
            value: coordenadoresResponsaveisNomes[0] ?? "",
            onChange: handleCoordenadorChange,
            placeholder: coordenadorHint,
            options: coordenadorDropdown.length
              ? coordenadorDropdown
              : undefined,
          }}
        />
      )}

      <TextFieldRow
        left={{
          label: "Número da O.S.",
          value: numeroOS,
          onChange: toUpper(setNumeroOS),
          placeholder: "Digite o número da O.S.",
        }}
        right={{
          label: "Nome da Embarcação",
          value: nomeEmbarcacao,
          onChange: toUpper(setNomeEmbarcacao),
          placeholder: "Digite o nome da embarcação",
        }}
      />
    </div>
  );
};

type ConsumoProps = {
  responsavelColeta: string;
  setResponsavelColeta: (v: string) => void;
  motivoConsumo: string;
  setMotivoConsumo: (v: string) => void;
};

const ConsumoMovement: React.FC<ConsumoProps> = ({
  responsavelColeta,
  setResponsavelColeta,
  motivoConsumo,
  setMotivoConsumo,
}) => {
  const [colabOptions, setColabOptions] = useState<string[]>([]);
  const [loadingColabs, setLoadingColabs] = useState(false);
  const [erroColabs, setErroColabs] = useState<string | null>(null);

  const toUpper = (fn: (v: string) => void) => (v: string) =>
    fn((v || "").toUpperCase());

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    (async () => {
      setLoadingColabs(true);
      setErroColabs(null);

      try {
        const res = await fetch(COLABS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: ctrl.signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();

        const list: string[] = Array.isArray(json?.data)
          ? (json.data as Array<{ nome_completo?: string }>).map((x) =>
              (x?.nome_completo ?? "").trim()
            )
          : [];

        const unique = Array.from(
          new Set(
            list
              .map((s) => (typeof s === "string" ? s.toUpperCase() : ""))
              .filter(Boolean)
          )
        );

        if (alive) setColabOptions(unique);
      } catch (e: any) {
        if (alive) setErroColabs(String(e?.message || e));
      } finally {
        if (alive) setLoadingColabs(false);
      }
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  const colabDropdown = useMemo(
    () => (colabOptions.length ? colabOptions : undefined),
    [colabOptions]
  );

  return (
    <div>
      <TextFieldRow
        center={{
          label: "Destinatário da Coleta",
          value: responsavelColeta,
          onChange: toUpper(setResponsavelColeta),
          placeholder: loadingColabs
            ? "CARREGANDO COLABORADORES..."
            : erroColabs
            ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
            : "SELECIONE O DESTINATÁRIO DA COLETA",
          options: colabDropdown,
        }}
      />

      <TextFieldRow
        center={{
          label: "Para que será utilizado?",
          value: motivoConsumo,
          onChange: setMotivoConsumo,
          placeholder: "Digite aqui o motivo da coleta",
        }}
      />
    </div>
  );
};

type PerdaProps = {
  responsavelRemocao: string;
  setResponsavelRemocao: (v: string) => void;
  motivoPerda: string;
  setMotivoPerda: (v: string) => void;
};

const PerdaMovement: React.FC<PerdaProps> = ({
  motivoPerda,
  setMotivoPerda,
}) => {
  return (
    <div>
      <TextFieldRow
        center={{
          label: "Motivo da Perda",
          value: motivoPerda,
          onChange: setMotivoPerda,
          placeholder: "Ex.: produto vencido, avaria, extravio...",
        }}
      />
    </div>
  );
};

type VendaProps = {
  clienteNome: string;
  setClienteNome: (v: string) => void;

  nomeEmbarcacaoVenda: string;
  setNomeEmbarcacaoVenda: (v: string) => void;

  numeroPV: string;
  setNumeroPV: (v: string) => void;
};

const VendaMovement: React.FC<VendaProps> = ({
  clienteNome,
  setClienteNome,
  nomeEmbarcacaoVenda,
  setNomeEmbarcacaoVenda,
  numeroPV,
  setNumeroPV,
}) => {
  const [empresaOptions, setEmpresaOptions] = useState<string[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [erroEmpresas, setErroEmpresas] = useState<string | null>(null);

  const toUpper = (fn: (v: string) => void) => (v: string) =>
    fn((v || "").toUpperCase());

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    (async () => {
      setLoadingEmpresas(true);
      setErroEmpresas(null);

      try {
        const res = await fetch(EMPRESAS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: ctrl.signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();

        const list: string[] = Array.isArray(json?.data)
          ? (json.data as string[])
          : [];

        const unique = Array.from(
          new Set(
            list
              .map((s) => (typeof s === "string" ? s.trim().toUpperCase() : ""))
              .filter(Boolean)
          )
        );

        if (alive) setEmpresaOptions(unique);
      } catch (e: any) {
        if (alive) setErroEmpresas(String(e?.message || e));
      } finally {
        if (alive) setLoadingEmpresas(false);
      }
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  const empresaDropdown = useMemo(
    () => (empresaOptions.length ? empresaOptions : undefined),
    [empresaOptions]
  );

  return (
    <div>
      <TextFieldRow
        center={{
          label: "Nome do Cliente",
          value: clienteNome,
          onChange: toUpper(setClienteNome),
          placeholder: loadingEmpresas
            ? "CARREGANDO EMPRESAS..."
            : erroEmpresas
            ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
            : "SELECIONE O CLIENTE",
          options: empresaDropdown,
        }}
      />

      <TextFieldRow
        left={{
          label: "Nome da Embarcação",
          value: nomeEmbarcacaoVenda,
          onChange: toUpper(setNomeEmbarcacaoVenda),
          placeholder: "Digite o nome da embarcação",
        }}
        right={{
          label: "Número da P.V",
          value: numeroPV,
          onChange: toUpper(setNumeroPV),
          placeholder: "Digite o número da proposta de venda",
        }}
      />
    </div>
  );
};

const EstoqueMovement: React.FC = () => {
  return null;
};

const MovimentacaoEstoqueGenerico: React.FC = () => {
  const location = useLocation();

  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug?: string;
    estoqueSlug: string;
  }>();

  const { sessao } = useAuth();

  const config = useMemo(
    () =>
      getConfig({
        estoqueSlug,
        pathname: location.pathname,
      }),
    [estoqueSlug, location.pathname]
  );

  const moduloAtual = useMemo(
    () =>
      findModuloAtualFromSessao(
        sessao,
        areaSlug || "suprimentos",
        estoqueSlug || config.slug
      ),
    [sessao, areaSlug, estoqueSlug, config.slug]
  );

  const headers: (keyof TableRow)[] = [
    "codigo",
    "nomeProduto",
    "quantidadeEstoque",
    "quantidadeSelecionada",
  ];

  const columnWidths = ["90px", "1fr", "92px", "104px"];

  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [rows, setRows] = useState<TableRow[]>([]);
  const [rowMetaByCodigo, setRowMetaByCodigo] = useState<
    Record<string, RowMeta>
  >({});
  const [codigo, setCodigo] = useState("");

  const [selected, setSelected] = useState<MovementType>(
    config.defaultMovement
  );

  const [numeroOS, setNumeroOS] = useState("");
  const [nomeEmbarcacao, setNomeEmbarcacao] = useState("");
  const [responsavelLiberacao, setResponsavelLiberacao] = useState("");
  const [responsavelColeta, setResponsavelColeta] = useState("");

  const [nomeEmpresa, setNomeEmpresa] = useState("");
  const [empresaClienteId, setEmpresaClienteId] = useState<number | null>(null);

  const [coordenadoresResponsaveisIds, setCoordenadoresResponsaveisIds] =
    useState<number[]>([]);
  const [coordenadoresResponsaveisNomes, setCoordenadoresResponsaveisNomes] =
    useState<string[]>([]);

  const [perdaResponsavelRemocao, setPerdaResponsavelRemocao] = useState("");
  const [perdaMotivo, setPerdaMotivo] = useState("");

  const [consumoResponsavelColeta, setConsumoResponsavelColeta] = useState("");
  const [consumoMotivo, setConsumoMotivo] = useState("");

  const [vendaClienteNome, setVendaClienteNome] = useState("");
  const [vendaNomeEmbarcacao, setVendaNomeEmbarcacao] = useState("");
  const [vendaNumeroPV, setVendaNumeroPV] = useState("");

  useEffect(() => {
    setSelected(config.defaultMovement);
    resetForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.key, config.slug]);

  const calcularCustoTotal = () => {
    return rows.reduce((acc, r) => {
      const meta = rowMetaByCodigo[r.codigo];
      const unit = moneyToNumber(meta?.custoUnitarioDefault);
      return acc + unit * r.quantidadeSelecionada;
    }, 0);
  };

  const resetForm = (keepSelected = true) => {
    setCodigo("");
    setRows([]);
    setRowMetaByCodigo({});

    setNumeroOS("");
    setNomeEmbarcacao("");
    setResponsavelLiberacao("");
    setResponsavelColeta("");

    setNomeEmpresa("");
    setEmpresaClienteId(null);
    setCoordenadoresResponsaveisIds([]);
    setCoordenadoresResponsaveisNomes([]);

    setPerdaMotivo("");
    setPerdaResponsavelRemocao("");

    setConsumoMotivo("");
    setConsumoResponsavelColeta("");

    setVendaClienteNome("");
    setVendaNomeEmbarcacao("");
    setVendaNumeroPV("");

    if (!keepSelected) {
      setSelected(config.defaultMovement);
    }
  };

  const renderMovementComponent = () => {
    switch (selected) {
      case "Serviço":
        return (
          <ServicoMovement
            enableEmpresaCoordenacao={config.enableServicoEmpresaCoordenacao}
            numeroOS={numeroOS}
            setNumeroOS={(v) => setNumeroOS(v.toUpperCase())}
            nomeEmbarcacao={nomeEmbarcacao}
            setNomeEmbarcacao={(v) => setNomeEmbarcacao(v.toUpperCase())}
            responsavelLiberacao={responsavelLiberacao}
            setResponsavelLiberacao={(v) =>
              setResponsavelLiberacao(v.toUpperCase())
            }
            responsavelColeta={responsavelColeta}
            setResponsavelColeta={(v) => setResponsavelColeta(v.toUpperCase())}
            nomeEmpresa={nomeEmpresa}
            setNomeEmpresa={(v) => setNomeEmpresa(v.toUpperCase())}
            empresaClienteId={empresaClienteId}
            setEmpresaClienteId={setEmpresaClienteId}
            coordenadoresResponsaveisIds={coordenadoresResponsaveisIds}
            setCoordenadoresResponsaveisIds={setCoordenadoresResponsaveisIds}
            coordenadoresResponsaveisNomes={coordenadoresResponsaveisNomes}
            setCoordenadoresResponsaveisNomes={
              setCoordenadoresResponsaveisNomes
            }
          />
        );

      case "Estoque":
        return <EstoqueMovement />;

      case "Perda":
        return (
          <PerdaMovement
            responsavelRemocao={perdaResponsavelRemocao}
            setResponsavelRemocao={(v) =>
              setPerdaResponsavelRemocao(v.toUpperCase())
            }
            motivoPerda={perdaMotivo}
            setMotivoPerda={(v) => setPerdaMotivo(v.toUpperCase())}
          />
        );

      case "Consumo":
        return (
          <ConsumoMovement
            responsavelColeta={consumoResponsavelColeta}
            setResponsavelColeta={(v) =>
              setConsumoResponsavelColeta(v.toUpperCase())
            }
            motivoConsumo={consumoMotivo}
            setMotivoConsumo={(v) => setConsumoMotivo(v.toUpperCase())}
          />
        );

      case "Venda":
        return (
          <VendaMovement
            clienteNome={vendaClienteNome}
            setClienteNome={(v) => setVendaClienteNome(v.toUpperCase())}
            nomeEmbarcacaoVenda={vendaNomeEmbarcacao}
            setNomeEmbarcacaoVenda={(v) =>
              setVendaNomeEmbarcacao(v.toUpperCase())
            }
            numeroPV={vendaNumeroPV}
            setNumeroPV={(v) => setVendaNumeroPV(v.toUpperCase())}
          />
        );

      default:
        return null;
    }
  };

  const handleEnviarBuscarProduto = async () => {
    const code = codigo.trim().toUpperCase();

    if (!code || buscando) return;

    if (!moduloAtual?.id) {
      alert("Não foi possível identificar o módulo de estoque desta rota.");
      return;
    }

    setBuscando(true);

    try {
      const payload = {
        modulo_id: moduloAtual.id,
        codigo: code,
      };

      const { resp, json } = await postJsonAuth<GetProdutoResp>(
        URL_GET_PRODUTO,
        payload
      );

      const safeJson: GetProdutoResp = json ?? {
        ok: resp.ok,
        data: null,
        visiveis: null,
        ocultos: null,
      };

      if (!resp.ok || safeJson.ok === false) {
        const msg =
          safeJson.erro ||
          (Array.isArray(safeJson.erros) && safeJson.erros.join(" | ")) ||
          `Falha (${resp.status})`;

        alert(msg);
        return;
      }

      const produtos = Array.isArray(safeJson.data)
        ? safeJson.data
        : Array.isArray(safeJson.visiveis)
        ? safeJson.visiveis
        : [];

      const produto =
        produtos.find(
          (p) =>
            String(p.codigo || "")
              .trim()
              .toUpperCase() === code
        ) ?? produtos[0];

      if (!safeJson.ok || !produto) {
        alert("Produto não encontrado.");
        return;
      }

      const quantidadeAtual = Number(produto.quantidade_atual ?? 0);

      if (!Number.isFinite(quantidadeAtual) || quantidadeAtual <= 0) {
        alert("Produto sem saldo disponível para movimentação.");
        return;
      }

      const custoDefault: string | null =
        produto.custo_unitario_atual !== undefined &&
        produto.custo_unitario_atual !== null
          ? String(produto.custo_unitario_atual)
          : produto.custo_unitario !== undefined &&
            produto.custo_unitario !== null
          ? String(produto.custo_unitario)
          : null;

      const produtoId = Number(produto.id);

      if (!Number.isInteger(produtoId) || produtoId <= 0) {
        alert("Produto retornado sem ID válido.");
        return;
      }

      const novaLinha: TableRow = {
        codigo: String(produto.codigo || "").toUpperCase(),
        nomeProduto: String(produto.nome || "").toUpperCase(),
        quantidadeEstoque: quantidadeAtual,
        quantidadeSelecionada: 1,
      };

      setRows((prev) => {
        if (prev.some((r) => r.codigo === novaLinha.codigo)) {
          alert("Produto já está na tabela.");
          return prev;
        }

        return [...prev, novaLinha];
      });

      setRowMetaByCodigo((prev) => ({
        ...prev,
        [novaLinha.codigo]: {
          productId: produtoId,
          custoUnitarioDefault: custoDefault,
        },
      }));

      setCodigo("");
    } catch (e) {
      console.error(e);
      alert("Falha ao buscar o produto.");
    } finally {
      setBuscando(false);
    }
  };

  const buildItensPayload = () => {
    const now = nowAsSql();

    return rows.map((r) => {
      const meta = rowMetaByCodigo[r.codigo];

      if (!meta) {
        throw new Error(`Metadados ausentes para o código ${r.codigo}`);
      }

      const custoUnitario = meta.custoUnitarioDefault ?? null;

      return {
        produto_id: meta.productId,
        quantidade_selecionada: r.quantidadeSelecionada,
        ...(custoUnitario ? { custo_unitario: custoUnitario } : {}),
        data_item: now,
      };
    });
  };

  const buildCommonPayload = (
    tipoSaida: TipoSaida,
    itens: ReturnType<typeof buildItensPayload>
  ) => {
    if (!moduloAtual?.id) {
      throw new Error("Módulo de estoque não identificado.");
    }

    return {
      modulo_id: moduloAtual.id,
      tipo_saida: tipoSaida,
      departamento_origem: config.departamentoOrigem,
      estoque_slug: config.slug,
      estoqueSlug: config.slug,
      itens,
    };
  };

  const handleFinalizarMovimentacao = async () => {
    if (!rows.length) {
      alert("Adicione pelo menos um produto.");
      return;
    }

    if (!moduloAtual?.id) {
      alert("Não foi possível identificar o módulo de estoque desta rota.");
      return;
    }

    const tipoSaida = movementToTipoSaida(selected);

    if (!tipoSaida) {
      alert("Tipo de movimentação não suportado neste fluxo.");
      return;
    }

    if (enviando) return;

    setEnviando(true);

    try {
      const itens = buildItensPayload();
      const custoTotal = calcularCustoTotal().toFixed(2);

      let payload: any = {
        ...buildCommonPayload(tipoSaida, itens),
        custo_total: custoTotal,
      };

      if (selected === "Serviço") {
        if (!responsavelColeta.trim()) {
          alert("Informe o destinatário da coleta.");
          return;
        }

        if (!responsavelLiberacao.trim()) {
          alert("Informe o responsável pela liberação.");
          return;
        }

        payload = {
          ...payload,

          ...(numeroOS ? { os_numero: numeroOS.trim().toUpperCase() } : {}),

          coletor_nome: responsavelColeta.trim().toUpperCase(),

          responsavel_liberacao: responsavelLiberacao.trim().toUpperCase(),
          liberador_nome: responsavelLiberacao.trim().toUpperCase(),

          ...(nomeEmbarcacao
            ? { embarcacao: nomeEmbarcacao.trim().toUpperCase() }
            : {}),

          ...(nomeEmpresa
            ? {
                nomeEmpresa: nomeEmpresa.trim().toUpperCase(),
                empresa_cliente: nomeEmpresa.trim().toUpperCase(),
              }
            : {}),

          ...(empresaClienteId != null
            ? { empresa_cliente_id: empresaClienteId }
            : {}),

          ...(coordenadoresResponsaveisIds.length > 0
            ? { coordenador_responsavel_ids: coordenadoresResponsaveisIds }
            : {}),

          ...(coordenadoresResponsaveisNomes.length > 0
            ? {
                coordenador_responsavel_nomes: coordenadoresResponsaveisNomes,
              }
            : {}),
        };
      }

      if (selected === "Perda") {
        if (!perdaMotivo.trim()) {
          alert("Informe o motivo da perda.");
          return;
        }

        payload = {
          ...payload,
          motivo: perdaMotivo.trim().toUpperCase(),
          responsavel_remocao: perdaResponsavelRemocao.trim().toUpperCase(),
        };
      }

      if (selected === "Consumo") {
        if (!consumoResponsavelColeta.trim()) {
          alert("Informe o nome do responsável pela coleta.");
          return;
        }

        if (!consumoMotivo.trim()) {
          alert("Informe o motivo do consumo.");
          return;
        }

        payload = {
          ...payload,
          coletor_nome: consumoResponsavelColeta.trim().toUpperCase(),
          motivo: consumoMotivo.trim().toUpperCase(),
        };
      }

      if (selected === "Venda") {
        if (!vendaClienteNome.trim()) {
          alert("Informe o Nome do Cliente.");
          return;
        }

        if (!vendaNumeroPV.trim()) {
          alert("Informe o Número da P.V.");
          return;
        }

        payload = {
          ...payload,
          cliente_nome: vendaClienteNome.trim().toUpperCase(),
          empresa_cliente: vendaClienteNome.trim().toUpperCase(),
          proposta_venda: vendaNumeroPV.trim().toUpperCase(),
          embarcacao: (vendaNomeEmbarcacao || "").trim().toUpperCase() || "N/A",
        };
      }

      const { resp, json } = await postJsonAuth<SaidaMovimentarResp>(
        URL_POST_SAIDA_MOVIMENTAR,
        payload
      );

      const safeJson: SaidaMovimentarResp = json ?? { ok: resp.ok };

      if (!resp.ok || safeJson.ok === false) {
        const msg =
          safeJson.erro ||
          (Array.isArray(safeJson.erros) && safeJson.erros.join(" | ")) ||
          `Falha (${resp.status})`;

        alert(`Erro ao movimentar: ${msg}`);
        return;
      }

      const movimentacaoId = safeJson.data?.movimentacao?.id ?? "?";

      const label =
        selected === "Serviço"
          ? "Movimentação criada"
          : selected === "Perda"
          ? "Perda registrada"
          : selected === "Consumo"
          ? "Consumo registrado"
          : selected === "Venda"
          ? "Venda registrada"
          : "Movimentação registrada";

      alert(`${label} (id=${movimentacaoId}) — custo_total R$ ${custoTotal}`);

      resetForm();
    } catch (e: any) {
      console.error("Falha ao finalizar movimentação:", e);
      alert(e?.message || "Falha ao finalizar movimentação.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="movimentacao-generica" style={styles.page}>
      <style>{compactCss}</style>

      <div style={styles.shell}>
        <div style={styles.topBlock}>
          <NavbarMoment
            options={config.options}
            initialSelected={selected}
            onSelect={(value) => setSelected(value as MovementType)}
          />

          <CodeReaderField
            label=""
            value={codigo}
            onChange={(v) => setCodigo(v.toUpperCase())}
            onSubmit={handleEnviarBuscarProduto}
            placeholder={
              buscando
                ? "Buscando produto..."
                : "Digite ou escaneie o código do Produto"
            }
            UpperCase={true}
          />
        </div>

        <div style={styles.movementBlock}>{renderMovementComponent()}</div>

        <div style={styles.tableBlock}>
          <MovementTable
            headers={headers}
            rows={rows}
            columnWidths={columnWidths}
            onUpdateRows={setRows}
          />
        </div>

        <div className="movimentacao-actions" style={styles.actions}>
          <Button
            text={enviando ? "Processando..." : "Realizar Movimentação"}
            onClick={handleFinalizarMovimentacao}
            disabled={rows.length === 0 || enviando}
          />
        </div>
      </div>
    </div>
  );
};

export default MovimentacaoEstoqueGenerico;
